import {defineStore} from 'pinia'
import {useCookie} from "nuxt/app";
import {useStartupStore} from "~/store/sturtupStore.js";
import {useCurrencyStore} from "~/store/currencyStore.js";
import {useClientFetch} from "~/utils/useClientFetch.js";

const USER_AUTHORITATIVE_CLIENT_DATA_KEYS = new Set(['zip', 'servicePoint'])

export const useCartStore = defineStore('cart', {
    state: () => ({
        content: {},
        couponResponse: null,
        cartActive: false,
        initialized: false,
        /** When true, cart API calls ask for cart_supplementaries (checkout only). */
        includeSupplementaries: false,
        serviceLocations: [],
        clientDataRequestSeq: {},
        sources: {
            product_page_shop_the_look: 'product_page_shop_the_look',
            product_page_add_to_cart_button: 'product_page_add_to_cart_button',
            product_page_add_to_cart_fixed_button: 'product_page_add_to_cart_button',
            product_page_sample_button: 'product_page_sample_button',
            better_together_calculator: 'better_together_calculator',
            better_together_combo: 'better_together_combo',
            collection_page: 'collection_page',
            share_cart: 'share_cart',
            alternative_sample_main: 'alternative_sample_main',
            alternative_sample_alternative: 'alternative_sample_alternative',
            fix_fog_calculator: 'fix_fog_calculator',
            product_category_faq: 'product_category_faq',
        }
    }),
    getters: {
        cartIsActive: (state) => {
            return state.cartActive
        },
        getToken() {
            return this.getCartToken()
        },
        zipIsValid: (state) => {
            const zip = state.content?.client_data?.zip
            const country = state.content?.client_data?.country

            if (!zip || !country) {
                return false
            }

            const normalizedZip = zip.toString().replace(/\s+/g, '')
            const zipLength = normalizedZip.length

            return (country == 1 && zipLength === 5) || (country == 2 && zipLength === 4)
        },
    },
    actions: {
        /**
         * Apply cart API payload; keep previously loaded checkout upsells when
         * the response intentionally omits cart_supplementaries.
         */
        applyCartContent(response) {
            this.content = this.withKeptCheckoutFields(response)
            this.initialized = true
            return this.content
        },
        withKeptCheckoutFields(response) {
            return {
                ...response,
                cart_supplementaries: response.cart_supplementaries ?? this.content?.cart_supplementaries,
                shipping_price_to_terminal: response.shipping_price_to_terminal ?? this.content?.shipping_price_to_terminal,
                notification_price: response.notification_price ?? this.content?.notification_price,
            }
        },
        obref() {
            if (typeof document === 'undefined') {
                return ''
            }

            const match = document.cookie.match(/(?:^|; )__obref=([^;]*)/)

            return match ? decodeURIComponent(match[1]) : ''
        },
        cartQueryExtras() {
            return this.includeSupplementaries ? {include_supplementaries: 1} : {}
        },
        cartBodyExtras() {
            return this.includeSupplementaries ? {include_supplementaries: true} : {}
        },
        getCartToken() {
            if (typeof localStorage === 'undefined') {
                return
            }

            let token = localStorage?.getItem('cart_token')

            if (!token) {
                token = useCookie('cart_token').value

                if (token) {
                    this.setCartToken(token)
                }
            }

            // setTimeout(() => {
            //     let token = getData('cart_token')
            //     console.log(token)
            // }, 3000)

            return token
        },
        setCartToken(token) {
            if (token) {
                localStorage?.setItem('cart_token', token)

                useCookie('cart_token', {
                    path: '/',
                    maxAge: 60 * 60 * 24 * 365 * 10,
                    sameSite: 'Lax'
                }).value = token
            }
        },
        clearAfterOrder() {
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('cart_token')
            }

            useCookie('cart_token', {
                path: '/',
                maxAge: 60 * 60 * 24 * 365 * 10,
                sameSite: 'Lax'
            }).value = null

            this.content = {}
            this.initialized = false
            this.cartActive = false
            this.includeSupplementaries = false
            this.couponResponse = null
        },
        randString(length) {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < length; i++) {
                const randomIndex = Math.floor(Math.random() * chars.length);
                result += chars[randomIndex];
            }
            return result;
        },
        openCart() {
            this.cartActive = true

            if (typeof (dataLayer) != "undefined" && dataLayer !== null) {
                let items = this.content.products?.map(product => {
                    return {
                        item_name: product.attributes.sku + ' - ' + product.name,
                        item_id: product.attributes.sku,
                        price: product.attributes.price_per_box ?? product.price,
                        item_brand: product.attributes.supplier_name,
                        item_category: product.attributes.group_category,
                        quantity: product.quantity
                    }
                })

                dataLayer.push({ecommerce: null});
                dataLayer.push({
                    event: "view_cart",
                    'currency': 'SEK',
                    'value': Math.ceil(this.content.total_sum / useCurrencyStore().rate),
                    ecommerce: {
                        items: items
                    }
                });
            }
        },
        toggleCart() {
            this.cartActive = !this.cartActive
        },
        getContent(hash, type, callback = null, options = {}) {
            if (options.includeSupplementaries) {
                this.includeSupplementaries = true
            }

            let token = this.getCartToken();

            if (hash && !token) {
                token = this.randString(25);
                this.setCartToken(token);
            }

            if (!token && !hash) return Promise.resolve(null)

            const startTime = Date.now(); // Start time

            return useClientFetch('/cart', {
                query: {cart_token: token, hash, type, ...this.cartQueryExtras()},
            }).then(response => {
                this.applyCartContent(response);

                if (!token) {
                    this.setCartToken(response.token);
                }

                const endTime = Date.now(); //Finish time
                const duration = endTime - startTime;
                console.log(`Successful request  ${duration} ms`);

                if (callback) {
                    callback(response);
                }
            })
        },
        addItemsToCart(items) {
            const token = this.getCartToken()

            useClientFetch('/cart/add-many', {
                method: 'POST',
                body: {
                    items: items,
                    cart_token: token,
                    obref: this.obref(),
                    ...this.cartBodyExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)
                const items = this.content.items

                if (!token) {
                    this.setCartToken(response.token)
                }

                if (items?.length && typeof (dataLayer) != "undefined" && dataLayer !== null) {
                    dataLayer.push({ecommerce: null});  // Clear the previous ecommerce object.
                    dataLayer.push({
                        'event': 'addToCart',
                        'ecommerce': {
                            'currencyCode': '' + useCurrencyStore().currency,
                            'add': {
                                'products': items.map(item => (
                                    {
                                        'name': item.name,
                                        'id': item.sku,
                                        'price': item.price,
                                        'brand': item.brand,
                                        'category': item.category,
                                        'quantity': item.quantity,
                                        'item_list_id': item.source,
                                        'item_list_name': item.source
                                    }
                                ))
                            }
                        }
                    });

                    dataLayer.push({ecommerce: null});
                    dataLayer.push({
                        'event': 'add_to_cart',
                        'currency': 'SEK',
                        'value': Math.ceil(response.total_sum / useCurrencyStore().rate),
                        'ecommerce': {
                            'currency': useCurrencyStore().currency,
                            'value': Math.round(items.reduce((acc, item) => acc + item.price * item.quantity, 0) * 100) / 100,
                            'items': items.map(item => {
                                return {
                                    'item_id': item.sku,
                                    'item_name': item.name,
                                    'item_brand': item.brand,
                                    'item_category': item.category,
                                    'item_category2': item.category2,
                                    'item_category3': item.category3,
                                    'currency': useCurrencyStore().currency,
                                    'price': item.price,
                                    'quantity': item.quantity,
                                    'item_list_id': this.sources.better_together_calculator,
                                    'item_list_name': this.sources.better_together_calculator
                                }
                            })
                        }
                    });
                }

                this.openCart()
            });
        },
        addItemToCart({id, count, sample, alternative = false, source, open = true}) {
            const token = this.getCartToken()

            return useClientFetch('/cart/add', {
                method: 'POST',
                query: {
                    product_id: id,
                    count: count,
                    sample: sample,
                    alternative: alternative ? 1 : 0,
                    source: source,
                    cart_token: token,
                    obref: this.obref(),
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)
                const item = this.content.item

                if (!token) {
                    this.setCartToken(response.token)
                }

                if (open !== false) {
                    this.openCart()
                }

                if (item && typeof (dataLayer) != "undefined" && dataLayer !== null) {
                    dataLayer.push({ecommerce: null});  // Clear the previous ecommerce object.
                    dataLayer.push({
                        'event': 'addToCart',
                        'ecommerce': {
                            'currencyCode': '' + useCurrencyStore().currency,
                            'add': {                                // 'add' actionFieldObject measures.
                                'products': [{                        //  adding a product to a shopping cart.
                                    'name': item.name,
                                    'id': item.sku,
                                    'price': item.price,
                                    'brand': item.brand,
                                    'category': item.category,
                                    'quantity': item.quantity,
                                    'item_list_id': source,
                                    'item_list_name': source,
                                }]
                            }
                        }
                    });

                    dataLayer.push({ecommerce: null});
                    dataLayer.push({
                        'event': 'add_to_cart',
                        'currency': 'SEK',
                        'value': Math.ceil(response.total_sum / useCurrencyStore().rate),
                        'ecommerce': {
                            'currency': useCurrencyStore().currency,
                            'value': Math.round(item.price * item.quantity * 100) / 100,
                            'items': [{
                                'item_id': item.sku,
                                'item_name': item.name,
                                'item_brand': item.brand,
                                'item_category': item.category,
                                'item_category2': item.category2,
                                'item_category3': item.category3,
                                'currency': useCurrencyStore().currency,
                                'price': item.price,
                                'quantity': item.quantity,
                                'item_list_id': source,
                                'item_list_name': source,
                            }]
                        }
                    });
                }

                if (this.content.klaviyo_data) {
                    var _learnq = window._learnq;

                    if (_learnq) {
                        _learnq.push(["track", "Added to Cart", this.content.klaviyo_data]);
                    }
                }
            });
        },
        updateItem(id, count) {
            const token = this.getCartToken()

            useClientFetch('/cart/update', {
                method: 'POST',
                query: {
                    product_id: id,
                    count,
                    cart_token: token,
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)
            });
        },
        removeItem(id, is_sample) {
            const token = this.getCartToken()
            this.content.products = this.content.products.filter(product => product.id !== id)

            useClientFetch('/cart/remove', {
                method: 'POST',
                query: {
                    product_id: id,
                    sample: is_sample,
                    cart_token: token,
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)

                if (this.content.removed_item) {
                    dataLayer.push({ecommerce: null});  // Clear the previous ecommerce object.
                    dataLayer.push({
                        'event': 'remove_from_cart',
                        'currency': 'SEK',
                        'value': Math.ceil(response.total_sum / useCurrencyStore().rate),
                        'ecommerce': {
                            'items': [this.content.removed_item]
                        }
                    });
                }
            });
        },
        /**
         * Apply cart API response without letting a stale single-field save wipe typed client fields.
         * Non-empty local client_data/email win; server-derived city is kept from response.
         * preferRemoteKeys: the field just saved must use the API value (local "0" must not overwrite "1").
         */
        applyCartResponse(response, {preserveClientData = false, preferRemoteKeys = []} = {}) {
            if (!response) {
                return
            }

            const withSupplementaries = this.withKeptCheckoutFields(response)

            if (!preserveClientData || !this.content) {
                this.content = withSupplementaries
                this.initialized = true
                return
            }

            const localClientData = this.content.client_data ?? {}
            const remoteClientData = withSupplementaries.client_data ?? {}
            const mergedClientData = {...remoteClientData}
            const preferRemote = new Set(preferRemoteKeys)

            Object.entries(localClientData).forEach(([key, value]) => {
                if (USER_AUTHORITATIVE_CLIENT_DATA_KEYS.has(key)) {
                    mergedClientData[key] = value
                    return
                }

                if (preferRemote.has(key)) {
                    return
                }

                if (value !== null && value !== undefined && value !== '') {
                    mergedClientData[key] = value
                }
            })

            this.content = {
                ...withSupplementaries,
                email: this.content.email || withSupplementaries.email,
                client_data: mergedClientData,
            }
            this.initialized = true
        },
        saveEmail(email) {
            const token = this.getCartToken()

            return useClientFetch('/cart/save-email', {
                method: 'POST',
                query: {
                    email,
                    cart_token: token,
                    obref: this.obref(),
                }
            }).then(response => {
                this.applyCartResponse(response, {preserveClientData: true})

                if (email && window._learnq) {
                    window._learnq.push(['identify', {
                        '$email': this.content.email,
                        '$first_name': this.content.client_data?.first_name,
                        '$last_name': this.content.client_data?.second_name
                    }]);

                    let data = {
                        "$event_id": this.randString(25),
                        "$value": this.content.total_sum,
                        "ItemNames": this.content.products.map(item => item.name),
                        "CheckoutURL": this.content.share_cart_url,
                        "Categories": [],
                        "Items": this.content.products.map(item => {
                            return {
                                'ProductID': item.attributes.sku,
                                'SKU': item.attributes.sku,
                                'ProductName': item.name,
                                'Quantity': item.quantity,
                                'ItemPrice': item.price,
                                'RowTotal': item.sub_total,
                                'ProductURL': item.attributes.url,
                                'ImageURL': item.attributes.image.replaceAll(' ', '%20'),
                                'ProductCategories': item.attributes.category
                            }
                        })
                    };

                    window._learnq.push(["track", "Started Checkout", data]);
                }
            });
        },
        saveClientData(key, value) {
            const token = this.getCartToken()

            // Optimistic UI (radios/flags) before the round-trip; merge prefers remote for this key.
            if (this.content?.client_data) {
                this.content.client_data[key] = value
            }

            const requestId = (this.clientDataRequestSeq[key] ?? 0) + 1
            this.clientDataRequestSeq[key] = requestId

            return useClientFetch('/cart/save-client-data', {
                method: 'POST',
                query: {
                    key,
                    value,
                    cart_token: token
                }
            }).then(response => {
                if (this.clientDataRequestSeq[key] !== requestId) {
                    return
                }

                this.applyCartResponse(response, {
                    preserveClientData: true, savingKey: key,
                    preferRemoteKeys: [key],
                })
            });
        },
        syncCheckoutClientData() {
            const token = this.getCartToken()
            const clientData = this.content?.client_data ?? {}

            return useClientFetch('/cart/save-client-data', {
                method: 'POST',
                body: {
                    cart_token: token,
                    clientData: {
                        email: this.content?.email ?? '',
                        type: clientData.type ?? 'person',
                        phone: clientData.phone ?? '',
                        first_name: clientData.first_name ?? '',
                        second_name: clientData.second_name ?? '',
                        address: clientData.address ?? '',
                        zip: clientData.zip ?? '',
                        country: clientData.country ?? '',
                        city: clientData.city ?? '',
                        company_name: clientData.company_name ?? '',
                        company_number: clientData.company_number ?? '',
                        servicePoint: clientData.servicePoint ?? null,
                        delivery_notification: clientData.delivery_notification ?? 0,
                        truck_pick_up: clientData.truck_pick_up ?? 0,
                        comment: clientData.comment ?? '',
                        batch_number: clientData.batch_number ?? '',
                    }
                }
            }).then(response => {
                // Full sync: merge so empty payload fields cannot wipe non-empty local/server values.
                this.applyCartResponse(response, {preserveClientData: true})
                return response
            })
        },
        saveShipping(key) {
            const token = this.getCartToken()

            useClientFetch('/cart/save-shipping', {
                method: 'POST',
                query: {
                    key,
                    cart_token: token,
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)

                if (key !== 'pick_up') {
                    this.saveClientData('truck_pick_up', 0)
                }
            });
        },
        saveCoupon(coupon) {
            const token = this.getCartToken()

            useClientFetch('/cart/save-coupon', {
                method: 'POST',
                query: {
                    coupon,
                    cart_token: token,
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                if (response.status === 'ok') {
                    this.applyCartContent(response.data)
                }

                this.couponResponse = response.message

                setTimeout(() => {
                    this.couponResponse = null
                }, 3000)
            });
        },
        removeCoupon() {
            const token = this.getCartToken()

            useClientFetch('/cart/remove-coupon', {
                method: 'POST',
                query: {
                    cart_token: token,
                    ...this.cartQueryExtras(),
                }
            }).then(response => {
                this.applyCartContent(response)
            });
        },
        share(email) {
            const token = this.getCartToken()

            useClientFetch('/cart/share', {
                method: 'POST',
                query: {
                    email,
                    cart_token: token
                }
            });
        },
        saveServicePoint(servicePoint) {
            this.saveClientData('servicePoint', JSON.stringify(servicePoint))
        },
        getServiceLocations() {
            const zip = this.content?.client_data?.zip
            const countryId = this.content?.client_data?.country

            const country = useStartupStore().countries.find(c => c.id == countryId)?.code

            if (!zip || !country) {
                return Promise.resolve([])
            }

            const normalizedZip = zip.toString().replace(/\s+/g, '')

            return useClientFetch('/cart/service-points', {
                method: 'POST',
                query: {
                    zip: normalizedZip,
                    country
                }
            }).then(response => {
                this.serviceLocations = response.service_points ?? []
                return this.serviceLocations
            })
        },
        isReadyForPayment() {
            const formIsValid =
                this.content.email
                && this.content.client_data.first_name
                && this.content.client_data.second_name
                && this.content.client_data.phone
                && this.content.client_data.address
                && this.content.client_data.zip
                && this.content.client_data.country
                && this.content.shipping_code

            if (!formIsValid) {
                return false;
            }

            if (this.content.client_data.type === 'organization') {
                return this.content.client_data.company_name && this.content.client_data.company_number;
            }

            return true;
        }
    },
})
